import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  GameContainer,
  HandContainer,
  CardsContainer,
  CardWrapper,
  CardImage,
  ActionContainer,
  GameOverContainer,
  ResultMessage,
  ActionButton,
  NewGameButton,
  DoubleDownButton
} from '../styles/BlackjackGameStyles';

class BlackjackLogic {
  static calculateHandValue(hand: string[]): number {
    let value = 0;
    let aces = 0;
    for (const card of hand) {
      const rank = card.split(' of ')[0];
      if (['JACK', 'QUEEN', 'KING'].includes(rank)) {
        value += 10;
      } else if (rank === 'ACE') {
        aces += 1;
      } else {
        value += parseInt(rank);
      }
    }
    for (let i = 0; i < aces; i++) {
      if (value + 11 <= 21) {
        value += 11;
      } else {
        value += 1;
      }
    }
    return value;
  }
}

interface GameState {
  player_hands: string[][];
  dealer_hand: string[];
  current_player: number;
  game_over: boolean;
  results: string[];
  player_hand_values: number[];
  dealer_hand_value: number | null;
}

const BlackjackGame: React.FC = () => {
  const [gameId, setGameId] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const prevPlayerCardsCount = useRef(0);
  const prevDealerCardsCount = useRef(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [showAllDealerCards, setShowAllDealerCards] = useState(false);

  useEffect(() => {
    startNewGame();
  }, []);

  const startNewGame = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/start-game`, { num_players: 1 });
      setGameId(response.data.game_id);
      await fetchGameState(response.data.game_id);
    } catch (error) {
      setError('Failed to start a new game');
    }
  };

  const fetchGameState = async (id: string) => {
    try {
      console.log(`Fetching game state for game ID: ${id}`);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/game-state/${id}`);
      console.log('Received game state:', response.data);
      setGameState(response.data);
    } catch (error) {
      console.error('Error fetching game state:', error);
      setError('Failed to fetch game state');
    }
  };

  const playerAction = async (action: 'hit' | 'stand' | 'double_down') => {
    if (!gameId) {
      console.error('No game ID available');
      return;
    }
    try {
      console.log(`Performing ${action} action for game ID: ${gameId}`);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/player-action/${gameId}`, {
        player_id: 0,
        action: action
      });
      console.log('Player action response:', response.data);
      setGameState(response.data);  // Update the game state directly with the response
    } catch (error) {
      console.error('Error performing action:', error);
      setError('Failed to perform action');
    }
  };

  const getCardImage = (card: string) => {
    const [rank, suit] = card.split(' of ');
    console.log('Card:', card, 'Rank:', rank, 'Suit:', suit);
    const imagePath = `/card_images_png/${rank}_${suit}.png`;
    console.log('Image path:', imagePath);
    return imagePath;
  };

  useEffect(() => {
    if (gameState) {
      prevPlayerCardsCount.current = gameState.player_hands[0].length;
      prevDealerCardsCount.current = gameState.dealer_hand.length;
    }
  }, [gameState]);

  useEffect(() => {
    if (gameState?.game_over && !isFlipping) {
      setIsFlipping(true);
      setTimeout(() => {
        setIsFlipping(false);
        // Show all dealer cards after the flip animation
        setTimeout(() => setShowAllDealerCards(true), 100);
      }, 200); // Match this with the animation duration
    } else if (!gameState?.game_over) {
      setShowAllDealerCards(false);
    }
  }, [gameState?.game_over]);

  useEffect(() => {
    if (gameState) {
      console.log('Game state updated:', gameState);
    }
  }, [gameState]);

  const renderHand = (hand: string[], isDealer: boolean = false) => {
    const prevCount = isDealer ? prevDealerCardsCount.current : prevPlayerCardsCount.current;
    return (
      <HandContainer>
        <h3>{isDealer ? 'Dealer' : 'Player'}</h3>
        <CardsContainer>
          {hand.map((card, index) => (
            <CardWrapper 
              key={`${isDealer ? 'dealer' : 'player'}-${index}`} 
              $isNew={index >= prevCount}
              $isFlipping={isDealer && index === 0 && isFlipping}
              style={{
                opacity: isDealer && index >= 2 && !showAllDealerCards ? 0 : 1,
                transition: 'opacity 0.5s ease-in',
                transitionDelay: `${(index - 1) * 0.2}s`
              }}
            >
              {isDealer && index === 0 ? (
                <>
                  <CardImage
                    src='/card_images_png/BACK.png'
                    alt='Hidden card'
                    $isFlipped={gameState?.game_over}
                  />
                  <CardImage
                    src={getCardImage(card)}
                    alt={card}
                    $isFlipped={!gameState?.game_over}
                    style={{ position: 'absolute' }}
                  />
                </>
              ) : (
                <CardImage
                  src={getCardImage(card)}
                  alt={card}
                  onError={(e) => {
                    console.error('Error loading image:', e.currentTarget.src);
                    e.currentTarget.src = '/card_images_png/BACK.png'; // Fallback to back of card
                  }}
                />
              )}
            </CardWrapper>
          ))}
        </CardsContainer>
      </HandContainer>
    );
  };

  const renderResults = () => {
    if (!gameState || !gameState.game_over) {
      console.log("Game is not over yet");
      return null;
    }
    
    console.log("Game state:", gameState);
    
    const playerValue = gameState.player_hand_values[0]; // Assuming single player
    const dealerValue = gameState.dealer_hand_value;
    
    let resultExplanation = '';
    if (playerValue > 21) {
      resultExplanation = "You busted (went over 21). Dealer wins.";
    } else if (dealerValue && dealerValue > 21) {
      resultExplanation = "Dealer busted (went over 21). You win!";
    } else if (dealerValue && playerValue > dealerValue) {
      resultExplanation = `Your hand (${playerValue}) beats the dealer's hand (${dealerValue}). You win!`;
    } else if (dealerValue && playerValue < dealerValue) {
      resultExplanation = `Dealer's hand (${dealerValue}) beats your hand (${playerValue}). Dealer wins.`;
    } else if (dealerValue && playerValue === dealerValue) {
      resultExplanation = `It's a tie! Both you and the dealer have ${playerValue}.`;
    } else {
      resultExplanation = "Unexpected game state.";
    }
    
    return (
      <div>
        <ResultMessage>
          Your hand: {playerValue} | Dealer's hand: {dealerValue ?? 'Hidden'}
        </ResultMessage>
        <ResultMessage>{resultExplanation}</ResultMessage>
      </div>
    );
  };

  const resetGame = () => {
    setShowAllDealerCards(false);
    setIsFlipping(false);
    startNewGame();
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!gameState) {
    return <div>Loading...</div>;
  }

  console.log("Current game state:", gameState);

  return (
    <GameContainer>
      <h2>Blackjack Game</h2>
      {gameState && (
        <>
          {renderHand(gameState.dealer_hand, true)}
          {renderHand(gameState.player_hands[0])}
          {!gameState.game_over && (
            <ActionContainer>
              <ActionButton 
                onClick={() => playerAction('hit')} 
                disabled={gameState.current_player !== 0 || gameState.game_over}
              >
                Hit
              </ActionButton>
              <ActionButton 
                onClick={() => playerAction('stand')} 
                disabled={gameState.current_player !== 0}
              >
                Stand
              </ActionButton>
              <DoubleDownButton 
                onClick={() => playerAction('double_down')} 
                disabled={gameState.current_player !== 0 || gameState.player_hands[0].length !== 2}
              >
                Double Down
              </DoubleDownButton>
            </ActionContainer>
          )}
          {gameState.game_over && (
            <GameOverContainer>
              {renderResults()}
              <NewGameButton onClick={resetGame}>Start New Game</NewGameButton>
            </GameOverContainer>
          )}
        </>
      )}
    </GameContainer>
  );
};

export default BlackjackGame;